import React, { useState } from 'react';
import {
  Button, TextField, Typography, Box, Card, Grid, Alert, InputAdornment, IconButton,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SchoolIcon from '@mui/icons-material/School';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { useLoading } from '../Global/LoadingContext';
import { apiFetch, getClientPlatform, getOrCreateDeviceId } from '../Global/authService';
import AppSelect from '../UI/AppSelect';

interface LoginPageProps {
  onLogin: () => void;
}

const REGISTER_ROLES = ['COUNSELLOR', 'CERTIFICATE', 'FACULTY', 'STUDENT'];

const getReadableApiError = (status: number, raw: string) => {
  const trimmed = (raw || '').trim();
  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed) as { message?: string; error?: string; details?: string };
      const parsedMessage = String(parsed?.message || parsed?.details || '').trim();
      if (parsedMessage) {
        return parsedMessage;
      }
    } catch {
      return trimmed;
    }
  }

  if (status === 400) return 'Invalid registration data. Please check all fields.';
  if (status === 401) return 'Your session has expired. Please log in again.';
  if (status === 403) return 'Access denied for this action.';
  if (status === 409) return 'This username or mobile number already exists.';
  if (status >= 500) return 'Server error. Please try again in a moment.';
  return 'Registration failed. Please try again.';
};

const Login: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { setLoading } = useLoading();

  // ── Registration dialog state ────────────────────────────────────────────
  const [regOpen, setRegOpen] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regSuccess, setRegSuccess] = useState('');
  const [regError, setRegError] = useState('');
  const [regShowPassword, setRegShowPassword] = useState(false);
  const [branchOptions, setBranchOptions] = useState<string[]>([]);
  const [regForm, setRegForm] = useState({
    username: '', name: '', password: '', confirmPassword: '',
    role: '', branch: '', email: '', mobile: '',
  });

  const handleRegChange = (field: string) => (e: { target: { value: string } }) =>
    setRegForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleRegOpen = async () => {
    setRegForm({ username: '', name: '', password: '', confirmPassword: '', role: '', branch: '', email: '', mobile: '' });
    setRegError('');
    setRegSuccess('');
    setRegOpen(true);
    try {
      const res = await apiFetch('/contact');
      if (res.ok) {
        const data = await res.json();
        // Filter out 'ALL' — a user must belong to a real branch
        const branches: string[] = (data as { location: string }[])
          .map(b => b.location)
          .filter(l => l && l.toUpperCase() !== 'ALL');
        setBranchOptions(branches);
      }
    } catch {
      // If fetch fails, fall back to empty list (user sees no options but can still type)
      setBranchOptions([]);
    }
  };

  const handleRegSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (regForm.password !== regForm.confirmPassword) {
      setRegError('Passwords do not match.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email)) {
      setRegError('Please enter a valid email address.');
      return;
    }
    if (!/^\d{10}$/.test(regForm.mobile)) {
      setRegError('Mobile number must be exactly 10 digits.');
      return;
    }
    setRegSubmitting(true);
    try {
      const response = await apiFetch('/login/self-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: regForm.username.trim(),
          name: regForm.name.trim(),
          password: regForm.password,
          role: regForm.role,
          branch: regForm.branch.trim(),
          email: regForm.email.trim(),
          mobile: regForm.mobile.trim(),
        }),
      });
      if (response.ok || response.status === 201) {
        setRegSuccess('Registration submitted! An admin will review and activate your account.');
      } else {
        const text = await response.text();
        setRegError(getReadableApiError(response.status, text));
      }
    } catch {
      setRegError('An error occurred. Please try again later.');
    } finally {
      setRegSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const deviceId = getOrCreateDeviceId();
      const platform = getClientPlatform();
      const response = await apiFetch('/login/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, deviceId, platform }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', data.user.username);
        localStorage.setItem('name', data.user.name || data.user.username);
        localStorage.setItem('branch', data.user.branch);
        localStorage.setItem('role', data.user.role);
        onLogin();
      } else {
        const text = await response.text();
        setError(text || 'Invalid username or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        background: 'linear-gradient(145deg, #1e3c72 0%, #2a5298 50%, #d97706 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '60%',
          height: '60%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '50%',
          height: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
        },
      }}
    >
      <Grid container sx={{ minHeight: '100dvh', position: 'relative', zIndex: 1 }}>
        {/* Left Panel - Branding */}
        <Grid
          item
          xs={false}
          sm={6}
          md={6}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            px: 8,
          }}
        >
          <Box sx={{ maxWidth: 500 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 4,
                animation: 'fadeInLeft 0.8s ease-out',
                '@keyframes fadeInLeft': {
                  from: { opacity: 0, transform: 'translateX(-30px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  mr: 3,
                }}
              >
                <SchoolIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
              </Box>
              <Box>
                <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
                  NICT
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                  Admin Portal
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 3,
                lineHeight: 1.3,
                animation: 'fadeInLeft 0.8s ease-out 0.2s backwards',
              }}
            >
              Empowering Education Through Technology
            </Typography>

            <Typography
              variant="body1"
              sx={{
                opacity: 0.95,
                lineHeight: 1.8,
                fontSize: '1.1rem',
                animation: 'fadeInLeft 0.8s ease-out 0.4s backwards',
              }}
            >
              Manage your institution seamlessly with our comprehensive admin dashboard.
              Access student records, course management, certificates, and more.
            </Typography>

            <Box
              sx={{
                mt: 5,
                display: 'flex',
                gap: 4,
                animation: 'fadeInLeft 0.8s ease-out 0.6s backwards',
              }}
            >
              <Box>
                <Typography variant="h3" fontWeight={700}>
                  500+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Active Students
                </Typography>
              </Box>
              <Box>
                <Typography variant="h3" fontWeight={700}>
                  50+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Courses
                </Typography>
              </Box>
              <Box>
                <Typography variant="h3" fontWeight={700}>
                  10+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Branches
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Right Panel - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 4,
          }}
        >
          <Card
            elevation={24}
            sx={{
              width: '100%',
              maxWidth: 480,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              animation: 'fadeInUp 0.8s ease-out',
              '@keyframes fadeInUp': {
                from: { opacity: 0, transform: 'translateY(30px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Box sx={{ p: 5 }}>
              {/* Mobile Logo */}
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h4" fontWeight={800} sx={{ color: '#1e3c72' }}>
                  NICT
                </Typography>
              </Box>

              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                sx={{ color: '#1a202c', mb: 1 }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Enter your credentials to access your account
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: 24,
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: '#6b7280' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 4 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.75,
                    fontSize: '1rem',
                  }}
                >
                  Sign In to Dashboard
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" component="span">
                    New here?{' '}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AppRegistrationIcon />}
                    onClick={handleRegOpen}
                    sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                  >
                    Register
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ fontSize: '0.875rem' }}
                >
                  © {new Date().getFullYear()} NICT. All rights reserved.
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>

    {/* ── Self-Registration Dialog ────────────────────────────────────── */}
    <Dialog open={regOpen} onClose={() => !regSubmitting && setRegOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create an Account</DialogTitle>
      <Box component="form" onSubmit={handleRegSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          {regError && <Alert severity="error" sx={{ mb: 2 }}>{regError}</Alert>}
          {regSuccess ? (
            <Alert severity="success">{regSuccess}</Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Username" value={regForm.username}
                  onChange={handleRegChange('username')} inputProps={{ maxLength: 50 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Full Name" value={regForm.name}
                  onChange={handleRegChange('name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Password" type={regShowPassword ? 'text' : 'password'}
                  value={regForm.password} onChange={handleRegChange('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setRegShowPassword(p => !p)} edge="end" size="small">
                          {regShowPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Confirm Password" type="password"
                  value={regForm.confirmPassword} onChange={handleRegChange('confirmPassword')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppSelect required fullWidth label="Role" value={regForm.role}
                  onChange={handleRegChange('role')} options={REGISTER_ROLES} placeholder="Select a role" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppSelect required fullWidth label="Branch" value={regForm.branch}
                  onChange={handleRegChange('branch')}
                  options={branchOptions}
                  placeholder={branchOptions.length === 0 ? 'Loading branches…' : 'Select a branch'}
                  disabled={branchOptions.length === 0}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Email" type="email" value={regForm.email}
                  onChange={handleRegChange('email')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Mobile" value={regForm.mobile}
                  onChange={(e) => setRegForm(p => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRegOpen(false)} disabled={regSubmitting}>Cancel</Button>
          {!regSuccess && (
            <Button type="submit" variant="contained" disabled={regSubmitting}
              sx={{ minWidth: 120 }}>
              {regSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  </>
  );
};

export default Login;
